import {ContentChild, Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {DropdownDirectiveService} from './dropdown-directive.service';
import {DropdownContentDirective} from './dropdown-content.directive';
import {DropdownToggleDirective} from './dropdown-toggle.directive';
import {fromEvent, Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';

@Directive({
    selector: '[libDropdown]',
    exportAs: 'libDropdown',
    providers: [
        DropdownDirectiveService,
    ]
})
export class DropdownDirective implements OnInit, OnDestroy {
    /**
     * Default active status
     */
    @Input() defaultActive = false;

    /**
     * Allow hiding content if clicked outside of the dropdown
     */
    @Input() hideByOutsideClick = true;

    @Output() opened = new EventEmitter();
    @Output() closed = new EventEmitter();

    /**
     * The dropdown content component instance
     */
    @ContentChild(DropdownContentDirective) private content: DropdownContentDirective;
    @ContentChild(DropdownToggleDirective) private contentToggler: DropdownToggleDirective;

    public active$: Observable<boolean>;

    private subs: Subscription[] = [];

    constructor(
        private service: DropdownDirectiveService,
        private elementRef: ElementRef,
    ) {}

    ngOnInit() {
        if (!this.content) {
            throw new Error('Dropdown content is not specified');
        }

        if (!this.contentToggler) {
            throw new Error('Dropdown content toggler is not specified');
        }

        this.active$ = this.service.activeStatusChange$.pipe();

        this.service.active = this.defaultActive;

        if (this.hideByOutsideClick) {
            this.subscribeToOutsideClickEvent();
        }

        this.subs.push(
            this.service.activeStatusChange$.subscribe((active: boolean) => {
                if (active) {
                    this.opened.emit();
                } else {
                    this.closed.emit();
                }
            })
        );
    }

    /**
     * You should pass 'click' event to prevent it or preventing the 'click' event by yourself before calling,
     * to not cause closing by clickOutsideHandling
     * @param event - lorem
     */
    open(event?: Event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.service.active = true;
    }

    /**
     * You should pass 'click' event to prevent it or preventing the 'click' event by yourself before calling,
     * to not cause closing by clickOutsideHandling
     * @param event - Event
     */
    close(event?: Event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.service.active = false;
    }

    /**
     * You should pass 'click' event to prevent it or preventing the 'click' event by yourself before calling,
     * to not cause closing by clickOutsideHandling
     * @param event - Event
     */
    toggle(event?: Event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.service.toggleActive();
    }

    private subscribeToOutsideClickEvent() {
        this.subs.push(
            fromEvent(document, 'click').pipe(
                map((event: MouseEvent) => event.target),
                filter((target: HTMLElement) => !this.elementRef.nativeElement.contains(target))
            ).subscribe(() => {
                this.service.active = false;
            })
        );
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
    }
}
